import React, {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { Form, FormInstance, StepProps } from 'antd'
import useForm, { UseFormReturn } from '../../../hooks/use-form'
import { useStoreCampaign } from '../../hooks'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { AnalyzeJobOfferResult } from '../../../prompts/output/analyze-job-offer-result'
import { useAuth } from '../../../auth'
import { StopAndRateConversationResult } from '../../../prompts/output/stop-and-rate-conversation-result'
import { useBlocker, useNavigate } from '@tanstack/react-router'
import { ChatMessageProps } from '../../../components/chat/chat'
import { AgentType, INTERVIEW_AGENT } from '../../agent-type'
import {
    availableCampaignAgentTypes,
    CampaignCreateInitialValues,
    getCampaignAgentConfig,
    getCampaignCreateInitialValues,
} from './campaign-agent-config'
import { INDEX_MESSAGE_TEMPLATE, INDEX_PHONE_NUMBERS, INDEX_WHATSAPP_BUSINESS_ACCOUNT } from '../../../user/permission'

import { ToolActivity, ToolTestMode } from '../../../app-connection/types'
import { toolConfiguration } from '../../../app-connection/tool-configuration'
import { DEFAULT_CAMPAIGN_LOCALE } from '../../campaign-locale'
import { useCreateTryOutConversation } from './use-create-try-out-conversation'
import { useJobOfferAnalysis } from './use-job-offer-analysis'

export const TYPE = 'type'
export const DETAILS = 'details'
export const WHATSAPP_SETUP = 'whatsapp-setup'
export const TRY = 'try'
export const LINK = 'link'

export const campaignCreateSteps = [TYPE, DETAILS, WHATSAPP_SETUP, TRY, LINK] as const
export const campaignCreateStep = z.enum(campaignCreateSteps)

export type CampaignCreateStep = z.infer<typeof campaignCreateStep>

export type CampaignCreateStepsContextProps = {
    toolTestMode: ToolTestMode
    setToolTestMode: (mode: ToolTestMode) => void
    toolActivities: ToolActivity[]
    viewMatch: boolean
    setViewMatch: Dispatch<SetStateAction<boolean>>
    formProps: UseFormReturn<ReturnType<typeof useStoreCampaign>>
    currentStep: CampaignCreateStep
    current: number
    isPending: boolean
    nextStep: () => void
    previousStep: () => void
    openStep: (step: number) => void
    steps: (StepProps & { key: CampaignCreateStep })[]
    analyzeJobOfferResult?: AnalyzeJobOfferResult
    analyzedLocale?: string
    questionsNeedRegeneration: boolean
    isAnalyzing: boolean
    analyze: (feedback?: string) => Promise<void>
    editingJobOffer: boolean
    setEditingJobOffer: Dispatch<SetStateAction<boolean>>
    sendAssistantMessage: (message?: string) => Promise<void>
    isSendingAssistantMessage: boolean
    match: StopAndRateConversationResult | undefined
    messages: ChatMessageProps[]
    improve: () => void
    tryOut: boolean
    setTryOut: Dispatch<SetStateAction<boolean>>
    unlock: () => void
    availableTypes: CampaignAgentType[]
    selectedType?: CampaignAgentType
    noAvailableTypes: boolean
    hasTypeSelection: boolean
    selectType: (type: CampaignAgentType) => void
    activeAgentConfig?: ReturnType<typeof getCampaignAgentConfig>
    initialValues: CampaignCreateInitialValues
}

const CampaignCreateStepsContext = createContext<CampaignCreateStepsContextProps | undefined>(undefined)

export type CampaignCreateStepsProviderProps = {
    children: ReactNode
    current?: number
    openStep: (step: number) => void
}

export const CampaignCreateStepsContextProvider = ({ children, ...rest }: CampaignCreateStepsProviderProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { user, can } = useAuth()

    const mutation = useStoreCampaign()
    const formProps = useForm({
        mutation,
        notify: true,
        submitOnMetaEnter: false,
        onSuccess: (campaign) => {
            navigate({
                to: formProps.form.getFieldValue('publicFormEnabled')
                    ? '/campaigns/$campaignId/settings/candidate-sources'
                    : '/campaigns/$campaignId/board',
                params: {
                    campaignId: campaign.id,
                },
            })
        },
    })

    const [toolTestMode, setToolTestMode] = useState<ToolTestMode>('safe')
    const [tryOut, setTryOut] = useState(false)
    const [locked, setLocked] = useState(true)
    const [viewMatch, setViewMatch] = useState(false)
    const [isAdvancing, setIsAdvancing] = useState(false)
    const [reachedSteps, setReachedSteps] = useState<CampaignCreateStep[]>([])

    const availableTypes = useMemo(() => availableCampaignAgentTypes(can) as CampaignAgentType[], [can])
    const hasWhatsappPermissions = can([INDEX_WHATSAPP_BUSINESS_ACCOUNT, INDEX_PHONE_NUMBERS, INDEX_MESSAGE_TEMPLATE])
    const noAvailableTypes = availableTypes.length === 0
    const hasTypeSelection = availableTypes.length > 1

    const initialValues = useMemo(() => {
        return {
            ...getCampaignCreateInitialValues(availableTypes),
            locale: user?.locale || DEFAULT_CAMPAIGN_LOCALE,
            allowLanguageSwitch: false,
        }
    }, [availableTypes, user?.locale])

    const watchedLocale = Form.useWatch('locale', { form: formProps.form, preserve: true })
    const watchedType = Form.useWatch('type', formProps.form) as CampaignAgentType | undefined
    const selectedType = watchedType ?? (availableTypes.length === 1 ? availableTypes[0] : undefined)
    const activeAgentConfig = getCampaignAgentConfig(selectedType)

    const jobOfferAnalysis = useJobOfferAnalysis({
        form: formProps.form,
        agentConfig: activeAgentConfig,
        selectedType,
        locale: watchedLocale ?? initialValues.locale,
    })
    const conversation = useCreateTryOutConversation({
        form: formProps.form,
        agentConfig: activeAgentConfig,
        toolTestMode,
    })
    const { analyzeJobOfferResult, editingJobOffer, questionsNeedRegeneration } = jobOfferAnalysis
    const { reset: resetConversation } = conversation

    useEffect(() => {
        if (availableTypes.length === 1 && watchedType !== availableTypes[0]) {
            formProps.form.setFieldValue('type', availableTypes[0])
        }

        if (availableTypes.length > 1 && watchedType && !availableTypes.includes(watchedType)) {
            formProps.form.setFieldValue('type', undefined)
        }
    }, [availableTypes, formProps.form, watchedType])

    const resetTryOut = useCallback(() => {
        resetConversation()
        setViewMatch(false)
        setLocked(true)
    }, [resetConversation])

    const invalidateConversation = useCallback(() => {
        resetTryOut()
        setTryOut(false)
    }, [resetTryOut])

    useEffect(invalidateConversation, [selectedType, invalidateConversation])

    useBlocker({
        shouldBlockFn: () => {
            // Nothing to lose before the first change, so leaving needs no confirmation then.
            if (!locked || !formProps.form.isFieldsTouched()) {
                return false
            }

            return !window.confirm(t('Are you sure, you want to leave?'))
        },
    })

    const unlock = () => {
        setLocked(false)
    }

    const steps: (StepProps & { key: CampaignCreateStep })[] = [
        ...(hasTypeSelection || noAvailableTypes
            ? [
                  {
                      key: TYPE,
                      title: t('Type'),
                  } as StepProps & { key: CampaignCreateStep },
              ]
            : []),
        {
            key: DETAILS,
            title: t('Content'),
        },
        ...(hasWhatsappPermissions
            ? [
                  {
                      key: WHATSAPP_SETUP,
                      title: t('WhatsApp'),
                  } as StepProps & { key: CampaignCreateStep },
              ]
            : []),
        {
            key: TRY,
            title: t('Test conversation'),
        },
        {
            key: LINK,
            title: t('Candidate sources'),
        },
    ]

    const current = Math.min(rest.current || 0, Math.max(steps.length - 1, 0))
    const currentStep = steps[current]?.key || DETAILS
    const navigationDisabled =
        isAdvancing || mutation.isPending || jobOfferAnalysis.isAnalyzing || conversation.isSending

    const moveToStep = (step: number) => {
        setReachedSteps((reached) => [...new Set([...reached, currentStep, steps[step].key])])
        rest.openStep(step)
    }

    const advanceToStep = (step: number) => {
        if (navigationDisabled || step >= steps.length || step <= current) {
            return
        }

        if (!selectedType || !availableTypes.includes(selectedType)) {
            return
        }

        const detailsIndex = steps.findIndex(({ key }) => key === DETAILS)
        const whatsappIndex = steps.findIndex(({ key }) => key === WHATSAPP_SETUP)
        const needsContent = step > detailsIndex
        const needsWhatsapp = whatsappIndex >= 0 && step > whatsappIndex

        if (
            needsContent &&
            selectedType === INTERVIEW_AGENT &&
            (!analyzeJobOfferResult || editingJobOffer || questionsNeedRegeneration)
        ) {
            rest.openStep(detailsIndex)
            return
        }

        if (
            needsWhatsapp &&
            (!formProps.form.getFieldValue('whatsappBusinessAccountId') ||
                !formProps.form.getFieldValue('phoneNumberId') ||
                !formProps.form.getFieldValue('startMessageTemplateId'))
        ) {
            rest.openStep(whatsappIndex)
            return
        }

        if (needsContent) {
            setIsAdvancing(true)
            void formProps.form
                .validateFields([
                    'tools',
                    'title',
                    ['data', selectedType === INTERVIEW_AGENT ? 'vacancy' : 'prompt'],
                    'locale',
                ])
                .then(() => moveToStep(step))
                .catch(() => rest.openStep(detailsIndex))
                .finally(() => setIsAdvancing(false))
            return
        }

        moveToStep(step)
    }

    const openStep = (step: number) => {
        if (navigationDisabled || !Number.isInteger(step) || step === current || step < 0 || step >= steps.length) {
            return
        }

        if (step < current) {
            rest.openStep(step)
        } else if (reachedSteps.includes(steps[step].key)) {
            advanceToStep(step)
        }
    }

    const nextStep = () => advanceToStep(current + 1)
    const previousStep = () => openStep(current - 1)

    const startTryOut = () => {
        if (!activeAgentConfig || !user) {
            return
        }

        resetTryOut()
        setTryOut(true)
        conversation.bootstrap()
    }

    const selectType = (type: CampaignAgentType) => {
        formProps.form.setFieldValue('tools', [])
        setToolTestMode('safe')
        const config = getCampaignAgentConfig(type)
        if (!config || !availableTypes.includes(type) || type === selectedType) {
            return
        }

        formProps.form.setFieldValue('type', type)
        setReachedSteps([])
        formProps.form.setFieldValue('data', config.initialData)
        formProps.form.setFieldValue('whatsappBusinessAccountId', null)
        formProps.form.setFieldValue('phoneNumberId', null)
        formProps.form.setFieldValue('startMessageTemplateId', null)
        formProps.form.setFieldValue('recontactSchedule', [])
    }

    return (
        <CampaignCreateStepsContext.Provider
            value={{
                toolTestMode,
                toolActivities: conversation.toolActivities,
                setToolTestMode: (mode) => {
                    setToolTestMode(mode)
                    resetTryOut()
                    setTryOut(false)
                },
                setViewMatch,
                viewMatch,
                analyze: jobOfferAnalysis.analyze,
                sendAssistantMessage: conversation.sendAssistantMessage,
                match: conversation.match,
                messages: conversation.messages,
                isAnalyzing: jobOfferAnalysis.isAnalyzing,
                isSendingAssistantMessage: conversation.isSending,
                isPending: mutation.isPending,
                analyzeJobOfferResult,
                analyzedLocale: jobOfferAnalysis.analyzedLocale,
                questionsNeedRegeneration,
                editingJobOffer,
                setEditingJobOffer: jobOfferAnalysis.setEditingJobOffer,
                current,
                openStep,
                formProps,
                currentStep,
                previousStep,
                nextStep,
                steps: steps.map((step, index) => ({
                    ...step,
                    disabled:
                        navigationDisabled ||
                        index === current ||
                        (index > current && !reachedSteps.includes(step.key)),
                })),
                improve: () => {
                    resetTryOut()
                    setTryOut(false)
                    openStep(hasTypeSelection ? 1 : 0)
                },
                tryOut,
                availableTypes,
                selectedType,
                noAvailableTypes,
                hasTypeSelection,
                selectType,
                activeAgentConfig,
                initialValues,
                setTryOut: startTryOut,
                unlock,
            }}
        >
            <ConversationSettingsObserver
                form={formProps.form}
                onChange={invalidateConversation}
            />
            {children}
        </CampaignCreateStepsContext.Provider>
    )
}

// Keep per-keystroke subscriptions outside the shared wizard context.
function ConversationSettingsObserver({ form, onChange }: { form: FormInstance; onChange: () => void }) {
    const conversationSettings = Form.useWatch(
        (values) =>
            JSON.stringify({
                type: values.type,
                title: values.title,
                data: values.data,
                tools: values.type === 'custom' ? toolConfiguration(values.tools) : [],
                locale: values.locale,
                allowLanguageSwitch: values.allowLanguageSwitch,
                startMessageTemplateId: values.startMessageTemplateId,
            }),
        { form, preserve: true }
    )
    useEffect(onChange, [conversationSettings, onChange])

    return null
}

export const useCreateCampaignStepsContext = () => {
    const context = useContext(CampaignCreateStepsContext)

    if (!context) {
        throw new Error('useCreateContext must be used within a CreateContextProvider')
    }

    return context
}

type CampaignAgentType = AgentType
